/**
 * Payment Adapter Interface
 *
 * Contrato que TODO gateway (stub, ePayco, Wompi, Stripe, ...) debe cumplir.
 * Está reservado para el Sprint T1; el detalle de variantes vivirá en
 * `solutions/adr/ADR-T02_payment_adapter_pattern.md` cuando Solutions Lead
 * lo escriba.
 *
 * Diseño Day 1 (commerce-lead):
 * - Adapter es PURO: no toca DB ni edge functions directamente, recibe
 *   y devuelve types. El `checkoutClient` orquesta las llamadas HTTP
 *   y delega al adapter solo decisiones específicas del gateway
 *   (renderizado del form, validación local, etc.).
 * - Idempotency-Key se gestiona en `checkoutClient`, no en el adapter.
 * - Errores se propagan como rejected promises con `PaymentAdapterError`.
 */

export type Currency = 'COP';

export type Money = {
  amount_cents: number; // BIGINT en server, number en cliente (max safe ~9e15 cents)
  currency: Currency;
};

export type InitiatePaymentInput = {
  orderId: string;
  amount: Money;
  idempotencyKey: string; // UUID v4 — viaja en header Idempotency-Key del POST server-side
  customerEmail: string;
  metadata?: Record<string, unknown>;
};

export type InitiatePaymentResult = {
  /** Identificador del intent en el provider — opaque para el cliente. */
  externalReference: string;
  /**
   * Cómo el adapter quiere que el cliente proceda:
   * - 'render_form_inline' — el adapter expone un componente React que se renderiza inline
   * - 'redirect' — el cliente hace `window.location = redirectUrl`
   * - 'open_widget' — el adapter expone un método imperativo (ej. ePayco JS)
   * - 'stub_immediate' — solo para StubAdapter; resuelve directamente sin UI extra
   */
  flow: 'render_form_inline' | 'redirect' | 'open_widget' | 'stub_immediate';
  redirectUrl?: string;
  /**
   * Para flow='stub_immediate', el adapter puede devolver el resultado simulado
   * que el cliente envía al webhook stub para confirmar.
   */
  stubWebhookPayload?: Record<string, unknown>;
};

export type PaymentStatusResult = {
  status:
    | 'pending'
    | 'processing'
    | 'authorized'
    | 'captured'
    | 'failed'
    | 'expired'
    | 'voided'
    | 'requires_action';
  externalReference: string;
  /** Mensaje seguro para mostrar al usuario (ya redactado de PII). */
  userFacingMessage?: string;
};

export class PaymentAdapterError extends Error {
  public readonly code: string;
  public readonly retryable: boolean;
  constructor(code: string, message: string, retryable = false) {
    super(message);
    this.name = 'PaymentAdapterError';
    this.code = code;
    this.retryable = retryable;
  }
}

/**
 * Interface que cualquier adapter debe implementar.
 *
 * NO incluye lógica de UI — eso vive en `src/components/payment/` y se
 * elige según `result.flow` del initiate().
 */
export interface PaymentAdapter {
  /** Slug que matchea `taquilla.payment_provider_config.slug`. */
  readonly slug: string;
  readonly displayName: string;

  /**
   * Crea el payment intent en el provider y devuelve cómo proceder.
   * El server-side ya creó el row en `taquilla.payment` antes de llamar
   * a este método — el adapter NO crea el row, solo el intent remoto.
   */
  initiate(input: InitiatePaymentInput): Promise<InitiatePaymentResult>;

  /**
   * Polling helper opcional. Si el adapter soporta consulta de estado
   * directamente al provider (no via webhook), expone esto. Para MVP H5
   * solo lo usa el stub.
   */
  getStatus?(externalReference: string): Promise<PaymentStatusResult>;
}
